import React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import PerfectScrollbar from "react-perfect-scrollbar";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { makeStyles } from "@mui/styles";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";

import { MOBILE_NAV_BREAKPOINT } from "utils/breakpoints";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Typography from "@mui/material/Typography";

import MenuIcon from "@mui/icons-material/Menu";
import MenuOpen from "@mui/icons-material/MenuOpen";
import NavigateNext from "@mui/icons-material/NavigateNext";

import { SettingsDialog } from "components/common/dialogs";

import {
  setSidebarMini,
  setSidebarMiniUserPreference,
  toggleSidebarMiniUserPreference,
} from "store/reducers/uiReducer";

import componentStyles from "assets/theme/sidebar.js";


const useStyles = makeStyles(componentStyles);

export default function Sidebar({ routes = [], logo }) {
  const classes = useStyles();
  const location = useLocation();
  const dispatch = useDispatch();
  const [state, setState] = React.useState({});
  const miniActive = useSelector((state) => state.ui.sidebarMini);
  const [mouseEnter, setMouseEnter] = React.useState(false);
  const [settingsOpen, setSettingsOpen] = React.useState(false);

  React.useEffect(() => {
    setState(getCollapseStates(routes));
    // eslint-disable-next-line
  }, []);
  const toggleMiniActive = () => {
    // Clicking the icon is a user preference; persist it for the session.
    if (mouseEnter) {
      dispatch(setSidebarMiniUserPreference(false));
      setMouseEnter(false);
    } else {
      dispatch(toggleSidebarMiniUserPreference());
    }
  };
  // makes the sidenav normal on hover (actually when mouse enters on it)
  const onMouseEnterSidenav = () => {
    if (miniActive && !mouseEnter) {
      dispatch(setSidebarMini(false));
      setMouseEnter(true);
    }
  };
  // makes the sidenav mini on hover (actually when mouse leaves from it)
  const onMouseLeaveSidenav = () => {
    if (mouseEnter) {
      dispatch(setSidebarMini(true));
      setMouseEnter(false);
    }
  };
  // this creates the initial state of this component based on the collapse routes
  // that it gets through routes
  const getCollapseStates = (routes) => {
    let initialState = {};
    routes.map((prop) => {
      if (prop.collapse) {
        initialState = {
          [prop.state]: getCollapseInitialState(prop.views),
          ...getCollapseStates(prop.views),
          ...initialState,
        };
      }
      return null;
    });
    return initialState;
  };
  // this verifies if any of the collapses should be default opened on a rerender of this component
  // for example, on the refresh of the page,
  // while on the src/views/forms/RegularForms.js - route /admin/regular-forms
  const getCollapseInitialState = (routes) => {
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse && getCollapseInitialState(routes[i].views)) {
        return true;
      } else if (location.pathname.indexOf(routes[i].path) !== -1) {
        return true;
      }
    }
    return false;
  };
  // this is used on mobile devices, when a user navigates
  // the sidebar will autoclose
  const handleMenuClose = () => {
    if (window.innerWidth < 1200) {
      // toggleSidenav();
    }
  };
  // creates the links that appear in the left menu / Sidebar
  const createLinks = (routes) => {
    return routes.map((prop, idx) => {
      if (prop.redirect) {
        return null;
      } else if (prop.divider) {
        return <Divider key={`divider-${idx}`} classes={{ root: classes.divider }} />;
      } else if (prop.title) {
        if (miniActive) {
          return null;
        }
        return (
          <Typography
            key={`title-${prop.title || idx}`}
            variant="h6"
            component="h6"
            classes={{ root: classes.title }}
          >
            {prop.title}
          </Typography>
        );
      }
      /*
        Collapsed, the item renders only its icon (line below drops prop.name,
        and the icon itself is aria-hidden), so every nav link was nameless —
        the whole primary navigation, on every page. Naming the item rather than
        the icon keeps one label for both states: expanded, it matches the
        visible text, which is what "label in name" wants.
      */
      const itemLabel = prop.name;
      let textContent = (
        <>
          <Box minWidth="2.25rem" display="flex" alignItems="center">
            {typeof prop.icon === "string" ? (
              <Box
                component="i"
                className={prop.icon + " " + classes["text" + prop.iconColor]}
                marginLeft={miniActive ? "-.25rem" : ""}
              />
            ) : null}
            {typeof prop.icon === "object" ? (
              <Box
                component={prop.icon}
                width="1.25rem!important"
                height="1.25rem!important"
                marginLeft={miniActive ? "-.25rem" : ""}
                className={classes["text" + prop.iconColor]}
              />
            ) : null}
            {prop.icon === undefined && prop.miniName !== undefined ? (
              <Box
                component="span"
                className={classes["text" + prop.iconColor]}
              >
                {prop.miniName}
              </Box>
            ) : null}
          </Box>
          {miniActive ? null : prop.name}
        </>
      );
      if (prop.collapse) {
        const st = {};
        st[prop["state"]] = !state[prop.state];
        if (prop.multiStates) {
          prop.multiStates.forEach((item) => {
            st[item] = state[item];
          });
        }
        return (
          <React.Fragment key={`collapse-${prop.state || idx}`}>
            <ListItem
              component={"a"}
              href="#mui"
              aria-label={itemLabel}
              // It opens a submenu rather than navigating, so the state has to
              // be announced — otherwise activating it appears to do nothing.
              aria-expanded={Boolean(state[prop.state])}
              onClick={(e) => {
                e.preventDefault();
                setState(st);
              }}
              classes={{
                root: clsx(classes.listItemRoot, {
                  [classes.listItemRootCollapseActive]: getCollapseInitialState(
                    prop.views
                  ),
                }),
              }}
            >
              {textContent}
              {miniActive ? null : (
                <Box
                  component={NavigateNext}
                  marginLeft="auto"
                  width="1rem!important"
                  height="1rem!important"
                  className={clsx(classes.listItemRootCollapseIcon, {
                    [classes.listItemRootCollapseActiveIcon]: state[prop.state],
                  })}
                />
              )}
            </ListItem>
            <Collapse
              in={state[prop.state] && !miniActive}
              unmountOnExit
              className={classes.collapseRoot}
            >
              <List component="div" classes={{ root: classes.listRootCollapse }}>
                {createLinks(prop.views)}
              </List>
            </Collapse>
          </React.Fragment>
        );
      } else if (prop.isDialog) {
        return (
          <ListItem
            key={`dialog-${idx}`}
            component="a"
            href="#settings"
            aria-label={itemLabel}
            onClick={(e) => {
              e.preventDefault();
              handleMenuClose();
              setSettingsOpen(true);
            }}
            classes={{
              root: classes.listItemRoot,
              selected: classes.listItemSelected,
            }}
            button
          >
            {textContent}
          </ListItem>
        );
      } else if (prop.href) {
        return (
          <ListItem
            key={`href-${prop.href || idx}`}
            component={"a"}
            href={prop.href}
            aria-label={itemLabel}
            onClick={handleMenuClose}
            classes={{
              root:
                classes.listItemRoot +
                (prop.upgradeToPro
                  ? " " + classes.listItemRootUpgradeToPro
                  : ""),
              selected: classes.listItemSelected,
            }}
            target="_blank"
            selected={prop.upgradeToPro === true}
          >
            {textContent}
          </ListItem>
        );
      } else {
        return (
          !prop.hide && 
          <ListItem
            key={`link-${prop.layout}-${prop.path}-${idx}`}
            component={Link}
            aria-label={itemLabel}
            onClick={handleMenuClose}
            to={prop.layout + prop.path}
            classes={{
              root:
                classes.listItemRoot +
                (prop.upgradeToPro
                  ? " " + classes.listItemRootUpgradeToPro
                  : ""),
              selected: classes.listItemSelected,
            }}
            selected={
              location.pathname === prop.layout + prop.path ||
              prop.upgradeToPro === true
            }
          >
            {textContent}
          </ListItem>
        );
      }
    });
  };

  let logoImage = (
    <img alt={logo.imgAlt} className={classes.logoClasses} src={logo.imgSrc} style={{width:"auto", height:'2rem', objectFit: 'contain'}}/>
  );
  let logoObject =
    logo && logo.innerLink ? (
      <Link to={logo.innerLink} className={classes.logoLinkClasses}>
        {logoImage}
      </Link>
    ) : logo && logo.outterLink ? (
      <a href={logo.outterLink} className={classes.logoLinkClasses}>
        {logoImage}
      </a>
    ) : null;

  const desktopObject = (
    <>
      <Box
        padding={miniActive ? "0 0 0.5rem 0" : "0 0.75rem 0.5rem 1rem"}
        display="flex"
        justifyContent={miniActive ? "center" : "space-between"}
        alignItems="center"
      >
        {miniActive ? null : logoObject}
        <IconButton aria-label={miniActive ? "Expand sidebar" : "Collapse sidebar"} onClick={toggleMiniActive}>
          <Box
            component={miniActive || mouseEnter ? MenuOpen : MenuIcon}
            width="1.5rem!important"
            height="1.5rem!important"
          />
        </IconButton>
      </Box>
      <List component="div" classes={{ root: classes.listRoot }}>{createLinks(routes)}</List>
    </>
  );
  return (
    <>
        {/* Desktop only. The drawer is a fixed 250px (62px mini) with no
            responsive rule of its own, so below the navbar's mobile threshold it
            took most of a phone screen while the navbar was already showing its
            hamburger menu — two navigations competing for the same space. Below
            that width the navbar menu, the user-avatar menu and the footer carry
            every destination this drawer offers; e2e/mobile-nav.spec.js holds
            that line. Styling stays in the makeStyles classes; this only adds a
            display rule, deliberately, because this component is easy to break. */}
        <Drawer
          variant="permanent"
          anchor="left"
          open
          sx={(t) => ({ [t.breakpoints.down(MOBILE_NAV_BREAKPOINT)]: { display: 'none' } })}
          classes={{
            paper: clsx(classes.drawerPaperBase, { [classes.drawerDockedMiniActive]: miniActive }),
            docked: clsx(classes.drawerDockedBase, { [classes.drawerPaperMiniActive]: miniActive }),
          }}
          onMouseEnter={onMouseEnterSidenav}
          onMouseLeave={onMouseLeaveSidenav}
        >
          {navigator.platform.indexOf("Win") > -1 ? (
            <PerfectScrollbar>{desktopObject}</PerfectScrollbar>
          ) : (
            desktopObject
          )}
        </Drawer>
        <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  );
}

const commonProps = {
  name: PropTypes.string,
  // NOTE: you can either use miniName or icon, but not both
  // // // if you use both, only the icon will render
  miniName: PropTypes.string,
  icon: PropTypes.oneOfType([
    // this refers to icons such as ni ni-spaceship or fa fa-heart
    PropTypes.string,
    // this refers to icons from @mui/icons-material
    PropTypes.object,
  ]),
  iconColor: PropTypes.oneOf([
    "Primary",
    "PrimaryLight",
    "Error",
    "ErrorLight",
    "Warning",
    "WarningLight",
    "Info",
    "InfoLight",
    "Success",
    "SuccessLight",
    "Default",
  ]),
};

// this generates an anchor (<a href="href">..</a>) link
// this is a link that is sent outside the app
const hrefProp = PropTypes.shape({
  // if this is set to true, than the link will have an absolute position
  // use wisely and with precaution
  upgradeToPro: PropTypes.bool,
  href: PropTypes.string,
  ...commonProps,
});

// this generates a Link (<Link to="layout + path">..</Link>) link
// this is a link that is sent inside the app
const linkProp = PropTypes.shape({
  path: PropTypes.string,
  layout: PropTypes.string,
  component: PropTypes.func,
  hide: PropTypes.bool, // if this is set to true, than the link will have an absolute position
  ...commonProps,
});

const collapseProp = PropTypes.shape({
  collapse: true,
  // name of the collapse - needs to be unique
  state: PropTypes.string,
  // if you have multi level collapses,
  // you need to set this array to all of the
  // collapses you wish to keep open when opening
  // the multi level collapse
  multiStates: PropTypes.arrayOf(PropTypes.string),
  views: PropTypes.arrayOf(PropTypes.oneOfType([hrefProp, linkProp])),
  ...commonProps,
});

Sidebar.propTypes = {
  // NOTE: we recommend that your logo has the following dimensions
  // // 135x40 or 487x144 or a resize of these dimensions
  logo: PropTypes.shape({
    // innerLink is for links that will direct the user within the app
    // it will be rendered as <Link to="...">...</Link> tag
    innerLink: PropTypes.string,
    // outterLink is for links that will direct the user outside the app
    // it will be rendered as simple <a href="...">...</a> tag
    outterLink: PropTypes.string,
    // the image src of the logo
    imgSrc: PropTypes.string.isRequired,
    // the alt for the img
    imgAlt: PropTypes.string.isRequired,
  }),
  // links that will be displayed inside the component
  routes: PropTypes.arrayOf(
    PropTypes.oneOfType([
      hrefProp,
      linkProp,
      collapseProp,
      // this is just a title without any action on it
      // you can think of it as a disabled link
      PropTypes.shape({
        title: PropTypes.string,
      }),
      // this is just a divider line
      PropTypes.shape({
        divider: true,
      }),
    ])
  ),
};
